import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, output, Signal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { map, switchMap } from 'rxjs';
import { get, isUndefined, last } from 'lodash-es';
import { AttrPath } from '@cumlaude/metadata';
import { AanwezigheidMeasure, CompoundFilterExpression, DataService, ExportFilter, FilterExpression } from '../../../services/data.service';
import { FilterService } from '../../../services/filter.service';
import { UrlService } from '../../../services/url.service';
import { formatDurationUrenMinuten, getMaandLabelKort, getMaandLabelLang, getMaandLabelLangVoorlopig, isHuidigeMaand } from '@cumlaude/shared-utils';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { ButtonComponent, ToggleButtonComponent } from '@cumlaude/shared-components-buttons';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { VbarSeriesComponent, VPartitionData } from '../../../shared/dashboard/vbarchart-table/vbar-series/vbar-series.component';
import { createResponsiveBarWidth } from '../../../shared/dashboard/vbarchart-table/vbar-series/responsive-bar-width';
import { VbarStyle } from '../../../shared/dashboard/vbarchart-table/vbar-batch/vbar-batch.component';
import { Axis } from '../../../services/axis';
import { BarInfo } from '../../../services/stacked-bars';
import { AggregatorConfig } from '../../../shared/dashboard/base-dashboard/aggregator-config';
import { MultiAggregator, noAgg0 } from '../../../services/aggregation';
import { createAggFunctions } from '../../../shared/dashboard/base-dashboard/base-dashboard.component';
import { pathTo, unnest } from '../../../services/data-tree';
import { DataRequestContext } from '../../../shared/dashboard/base-dashboard/dashboard-context';
import { Attributes, createSelection, SelectionConfig } from '../../../shared/dashboard/base-dashboard/base-dashboard-config';
import { att } from '../../../services/measures';
import { getSchoolaanwezigheidClass, interventielaagForPerc } from '../../../services/schoolaanwezigheid';
import { ExportablePanel, TableExportPage } from '../../../services/exportable';
import { ExportOptions } from '../../../services/export.service';
import { QueryParamStateService } from '../../../services/query-param-state.service';
import { AfgeslotenLessenService } from '../../../services/afgesloten-lessen.service';
import { getFormattedValue } from '../../../shared/components/table/table/table.model';
import { formatDecimal } from '@cumlaude/shared-pipes';
import { UserService } from '../../../services/user.service';
import { MetricsSeriesComponent } from '../../../shared/dashboard/vbarchart-table/metrics-series/metrics-series.component';

export interface AanwezigheidI extends Attributes {
	aw_m_nr_lesuren: number;
	aw_m_nr_lesuren_aanwezig: number;
	aw_m_nr_minuten: number;
	aw_m_nr_minuten_aanwezig: number;
	aw_m_nr_leerlingen: number;
}

export interface AanwezigheidA extends Attributes {
	aw_m_nr_lesuren: number;
	aw_m_nr_lesuren_aanwezig: number;
	aw_m_nr_minuten: number;
	aw_m_nr_minuten_aanwezig: number;
	aw_m_nr_leerlingen: number;
}

export type AanwezigheidPerMaandPartitionData = VPartitionData & {
	exportData: {
		maand: string;
		percentage?: number;
		categorie?: string;
	};
};

@Component({
	selector: 'app-aanwezigheid-per-maand',
	templateUrl: './aanwezigheid-per-maand.component.html',
	styleUrls: ['./aanwezigheid-per-maand.component.scss'],
	imports: [IconComponent, ButtonComponent, TooltipDirective, VbarSeriesComponent, ToggleButtonComponent, MetricsSeriesComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AanwezigheidPerMaandComponent implements AggregatorConfig<AanwezigheidI, AanwezigheidA>, ExportablePanel {
	filterExpressions = input.required<FilterExpression[]>();
	permanentFilters = input.required<FilterExpression[]>();
	showDetailsButton = input(true);

	filter = computed(() => new CompoundFilterExpression([...this.filterExpressions(), ...this.permanentFilters()]));
	toonAfgeslotenLessen = signal(false);

	// Output voor dataRootSelection
	dataRootSelectionChange = output<ReturnType<typeof createSelection> | undefined>();

	private readonly filterService = inject(FilterService);
	private readonly dataService = inject(DataService);
	protected readonly urlService = inject(UrlService);
	protected readonly qp = inject(QueryParamStateService);
	private readonly afgeslotenLessenService = inject(AfgeslotenLessenService);
	private readonly userService = inject(UserService);

	private readonly elementRef = inject(ElementRef);

	readonly paddingLeft = 41;

	// Dynamische barWidth zodat de grafiek meeschaalt i.p.v. buiten het paneel te tekenen
	readonly barWidth = createResponsiveBarWidth(this.elementRef, 12, this.paddingLeft);

	readonly fixedGroups = signal<AttrPath[]>([['aw_nr_maand']]).asReadonly();
	readonly fixedSubgroups = signal<AttrPath[]>([]).asReadonly();

	schooljaar = toSignal(this.filterService.observeAsInput('aw_nm_schooljaar'));

	protected requestContext: Signal<DataRequestContext<AanwezigheidA> | undefined> = toSignal(
		toObservable(this.filter).pipe(
			switchMap((f) => {
				return this.dataService
					.getAanwezigheidData({
						g: [...this.fixedGroups(), ...this.fixedSubgroups()],
						f,
						r: [0],
						m: [AanwezigheidMeasure.LESUREN, AanwezigheidMeasure.LESTIJD, AanwezigheidMeasure.LEERLINGEN],
					})
					.pipe(
						map((resp) => {
							const { aggInit, aggCombine } = createAggFunctions(this, [], resp.measures);
							const records = unnest<AanwezigheidA, number[]>(resp.data, undefined, 1, aggInit, aggCombine);
							return {
								dataRoot: records[0]?.[0],
								groupNames: this.fixedGroups().map((p) => p.join('.')),
								subgroupNames: this.fixedSubgroups().map((p) => p.join('.')),
								measureNames: resp.measures,
								filter: f,
							};
						})
					);
			})
		)
	);

	loading = computed(() => this.requestContext() === undefined);
	loadingChanged = output<boolean>();

	partitions = computed<AanwezigheidPerMaandPartitionData[]>(() => {
		const dataRoot = this.requestContext()?.dataRoot;
		if (!dataRoot?.r) return [];

		// Zorg dat we altijd alle 12 maanden tonen van aug t/m jul, ook als er geen data voor is
		const maanden = Array.from({ length: 12 }, (_, i) => (((i + 7) % 12) + 1).toString());

		const partitions: AanwezigheidPerMaandPartitionData[] = maanden
			.map((maand) => ({ maand, path: dataRoot.r.find((lvl) => last(lvl)!.k === maand) }))
			.map(({ maand, path }) => {
				if (!path) {
					return {
						label: getMaandLabelKort(maand),
						className: 'onbekend',
						qty: 100,
						measure: { type: 'percentage', value: 1, visible: false },
						stacks: [[{ size: 1, className: 'onbekend', tooltip: 'Geen data beschikbaar' }]],
						path: [],
						exportData: {
							maand: getMaandLabelLang(maand),
						},
					};
				}
				const a = last(path)!.a;
				const frac = a.aw_m_nr_minuten_aanwezig / a.aw_m_nr_minuten;
				const nr_lesuren = a.aw_m_nr_lesuren ?? 0;
				const nr_lesuren_aanwezig = a.aw_m_nr_lesuren_aanwezig ?? 0;
				const perc = frac * 100;
				const categorie = interventielaagForPerc(frac);
				const voorlopig = isHuidigeMaand(this.schooljaar(), maand);
				const maandLabelLang = getMaandLabelLangVoorlopig(this.schooljaar(), maand);

				const stacks: BarInfo[][] = [
					[
						{
							size: perc,
							className: getSchoolaanwezigheidClass(this.schooljaar(), maand, categorie),
							text: `${getFormattedValue(frac, 'percentage', '1.0-0')}`,
							tooltip: [
								{ label: '', bold: true, value: maandLabelLang },
								`${getFormattedValue(frac, 'percentage', '1.2-2')} (${categorie})`,
								`${formatDecimal(nr_lesuren_aanwezig)} / ${formatDecimal(nr_lesuren)} lesuren`,
								`${formatDurationUrenMinuten(a.aw_m_nr_minuten_aanwezig, 'hu mm')} aanwezig`,
								`${formatDurationUrenMinuten(a.aw_m_nr_minuten - a.aw_m_nr_minuten_aanwezig, 'hu mm')} gemist`,
							],
							clickHandler: () => {
								this.qp.dispatchMulti({ 'lesuur-maand': Number(maand), 'reden-maand': Number(maand) });
							},
						},
					],
				];

				return {
					stacks,
					measure: { type: 'percentage', value: frac, visible: false },
					label: getMaandLabelKort(maand),
					qty: perc,
					path,
					className: voorlopig ? 'voorlopig' : undefined,
					exportData: {
						maand: maandLabelLang,
						percentage: perc,
						categorie,
					},
				};
			});
		return partitions;
	});

	afgeslotenLessen = this.afgeslotenLessenService.createAfgeslotenLessenSignal(this.filter, this.toonAfgeslotenLessen);

	dataRootSelection = computed(() => {
		const context = this.requestContext();
		if (isUndefined(context?.dataRoot)) return undefined;

		if (this.userService.isBestuur()) return undefined;

		return createSelection(context, pathTo(context.dataRoot), this.getSelectionConfig(context));
	});

	// Effect om dataRootSelection changes door te geven aan parent
	constructor() {
		effect(() => {
			this.dataRootSelectionChange.emit(this.dataRootSelection());
		});
		effect(() => {
			this.loadingChanged.emit(this.loading());
		});
	}

	async exportAsTable(exportOptions: ExportOptions, filters: ExportFilter[]): Promise<TableExportPage> {
		return {
			name: 'Aanwezigheid per maand',
			...(exportOptions.showTitle ? { title: 'Aanwezigheid per maand' } : {}),
			...(exportOptions.showFilters ? { filters } : {}),
			data: [
				{
					columns: [
						{ name: '', type: 'string' },
						{ name: 'Percentage', type: 'percentage', format: '1.0-0' },
						{ name: 'Categorie', type: 'string' },
					],
					rows: this.partitions().map((partition) => {
						const { maand, percentage, categorie } = get(partition, 'exportData');
						let perc = undefined;
						if (percentage) perc = percentage / 100;

						return [maand, perc, categorie];
					}),
				},
			],
		};
	}

	getSingleAggregators() {
		return {
			aw_m_nr_lesuren: noAgg0('aw_m_nr_lesuren'),
			aw_m_nr_lesuren_aanwezig: noAgg0('aw_m_nr_lesuren_aanwezig'),
			aw_m_nr_minuten: noAgg0('aw_m_nr_minuten'),
			aw_m_nr_minuten_aanwezig: noAgg0('aw_m_nr_minuten_aanwezig'),
			aw_m_nr_leerlingen: noAgg0('aw_m_nr_leerlingen'),
		};
	}
	getMultiAggregators(): MultiAggregator<keyof AanwezigheidA, AanwezigheidI, AanwezigheidA, number>[] {
		return [];
	}

	getSelectionConfig(_context: DataRequestContext<AanwezigheidA>): SelectionConfig<AanwezigheidA> {
		return {
			detailUrl: '/details/leerling/aanwezigheid',
			mergeQueryParams: false,
			provider: 'aanwezigheid',
			getSize: att('aw_m_nr_leerlingen'),
			extraFilters: [],
		};
	}

	readonly yAxis: Axis = {
		min: 0,
		max: 100,
		ticks: [
			{ label: '0%', qty: 0 },
			{ label: '50%', qty: 50 },
			{ label: '100%', qty: 100 },
		],
	};

	readonly Style = VbarStyle;
}
